import React, { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiSettings } from 'react-icons/fi';
import API from '../../Api/api';
import { MyContext } from '../../Context/MyContext';
import './AdminPages.css';

const DEFAULT_OPTIONS = [20, 50, 100];
const MIN_SIZE = 5;
const MAX_SIZE = 200;

const clampSize = (n, fallback = 20) => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return fallback;
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, v));
};

/** Ensure exactly 3 unique values in range (auto-bump duplicates). */
const ensureThreeUnique = (rawOptions = []) => {
  const out = [];
  for (let i = 0; i < 3; i += 1) {
    let v = clampSize(rawOptions[i], DEFAULT_OPTIONS[i] || 20);
    while (out.includes(v) && v < MAX_SIZE) v += 1;
    while (out.includes(v) && v > MIN_SIZE) v -= 1;
    if (out.includes(v)) v = clampSize((out[out.length - 1] || 20) + 10);
    out.push(clampSize(v));
  }
  return out.sort((a, b) => a - b);
};

const normalizeConfig = (raw = {}) => {
  const options = ensureThreeUnique(
    Array.isArray(raw.options) ? raw.options : DEFAULT_OPTIONS
  );
  const requestedDefault = clampSize(
    raw.default_page_size ?? raw.defaultSize ?? raw.default,
    options[0]
  );
  return {
    options,
    default: options.includes(requestedDefault) ? requestedDefault : options[0],
  };
};

const PropertyListPerPageSettings = () => {
  const { refreshPropertyListPageConfig } = useContext(MyContext) || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(() => normalizeConfig({}));
  const [saveMessage, setSaveMessage] = useState('');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      const res = await API.get('/admin/property-list-page-size');
      setConfig(normalizeConfig(res.data || {}));
    } catch (e) {
      setConfig(normalizeConfig({}));
      toast.error(e.response?.data?.error || 'Failed to load page-size settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const setOptionAt = (index, raw) => {
    setSaveMessage('');
    setConfig((prev) => {
      const options = [...prev.options];
      if (raw === '' || raw === '-' || raw === '.') {
        options[index] = '';
      } else {
        const num = Number(raw);
        options[index] = Number.isFinite(num) ? num : prev.options[index];
      }
      return { ...prev, options };
    });
  };

  const saveConfig = async (e) => {
    e?.preventDefault?.();
    if (saving) return;

    const options = ensureThreeUnique(config.options);
    const defaultValue = options.includes(Number(config.default))
      ? Number(config.default)
      : options[0];

    const payload = {
      options,
      default: defaultValue,
      default_page_size: defaultValue,
      defaultSize: defaultValue,
    };

    setSaving(true);
    setSaveMessage('');
    try {
      const res = await API.put('/admin/property-list-page-size', payload);
      const next = normalizeConfig(res.data || payload);
      setConfig(next);
      if (typeof refreshPropertyListPageConfig === 'function') {
        try {
          await refreshPropertyListPageConfig();
        } catch {
          /* non-fatal — settings already saved */
        }
      }
      setSaveMessage(`Saved: ${next.options.join(', ')} (default ${next.default})`);
      toast.success('Property list page-size settings saved');
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error
        || err.message
        || 'Failed to save page-size settings';
      const full = status ? `${msg} (HTTP ${status})` : msg;
      setSaveMessage(full);
      toast.error(full);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <form onSubmit={saveConfig}>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1><FiSettings style={{ marginRight: 8 }} />Property List Per Page</h1>
            <p>Edit the three page-size options shown on the public Property List, and choose which one is the default.</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={loadConfig} disabled={loading || saving}>
              <FiRefreshCw /> Refresh
            </button>
            <button type="submit" className="btn-primary" disabled={loading || saving}>
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>

        <div className="admin-card" style={{ maxWidth: 640 }}>
          {loading ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 8 }} />
          ) : (
            <div style={{ display: 'grid', gap: 18 }}>
              <div>
                <p style={{ margin: '0 0 12px', fontWeight: 600, color: '#111827' }}>
                  Page-size options
                </p>
                <div style={{ display: 'grid', gap: 12 }}>
                  {config.options.map((value, index) => {
                    const clampedPreview = value === '' ? null : clampSize(value, DEFAULT_OPTIONS[index]);
                    const isDefault = Number(config.default) === Number(clampedPreview);
                    return (
                      <div
                        key={`opt-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          flexWrap: 'wrap',
                          border: '1px solid var(--admin-border, #e5e7eb)',
                          borderRadius: 10,
                          padding: '12px 14px',
                          background: isDefault ? 'rgba(37,99,235,0.04)' : '#fff',
                        }}
                      >
                        <label
                          htmlFor={`property-list-size-${index}`}
                          style={{ minWidth: 72, fontSize: '0.875rem', color: '#64748b' }}
                        >
                          Option {index + 1}
                        </label>
                        <input
                          id={`property-list-size-${index}`}
                          type="number"
                          min={MIN_SIZE}
                          max={MAX_SIZE}
                          step={1}
                          value={value}
                          disabled={saving}
                          onChange={(e) => setOptionAt(index, e.target.value)}
                          style={{
                            width: 120,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: '1px solid var(--admin-border, #e5e7eb)',
                            fontSize: '0.95rem',
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#64748b' }}>per page</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', fontSize: '0.875rem', color: '#334155' }}>
                          <input
                            type="radio"
                            name="property-list-default-size"
                            checked={isDefault}
                            disabled={saving || value === '' || !Number.isFinite(Number(value))}
                            onChange={() => {
                              const n = clampSize(value, DEFAULT_OPTIONS[index]);
                              setConfig((prev) => ({ ...prev, default: n }));
                              setSaveMessage('');
                            }}
                          />
                          Default
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '0.8125rem', color: '#94a3b8' }}>
                  Allowed range: {MIN_SIZE}–{MAX_SIZE}. Duplicate values are adjusted automatically on save.
                </p>
              </div>

              {saveMessage ? (
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: saveMessage.startsWith('Saved') ? '#ecfdf5' : '#fef2f2',
                    color: saveMessage.startsWith('Saved') ? '#065f46' : '#991b1b',
                    fontSize: '0.875rem',
                  }}
                >
                  {saveMessage}
                </div>
              ) : null}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn-primary" disabled={loading || saving}>
                  {saving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default PropertyListPerPageSettings;
